/**
 * Drawdown 시계열 시각화 카드
 */
import { memo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DrawdownPoint } from '../../types/performance';
import {
  formatDateTick,
  formatPercentTick,
  formatTooltipLabel,
} from '../../utils/formatters';

interface DrawdownChartProps {
  data: DrawdownPoint[];
}

const DrawdownChart = memo(function DrawdownChart({ data }: DrawdownChartProps) {
  const hasData = data && data.length > 0;
  const latest = hasData ? data[data.length - 1].drawdown : null;

  return (
    <section className="performance-chart-shell">
      <header className="performance-chart-header">
        <div>
          <h3>Drawdown</h3>
          <p>
            누적 최대 낙폭과 최근 낙폭을 모니터링합니다.
          </p>
        </div>
        {latest !== null && (
          <span className="performance-chart-meta negative">
            현재 {latest.toFixed(2)}%
          </span>
        )}
      </header>
      {hasData ? (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 16, right: 24, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="drawdownGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#223548" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateTick}
              minTickGap={24}
              stroke="#7890a8"
            />
            <YAxis
              tickFormatter={formatPercentTick}
              width={80}
              stroke="#7890a8"
            />
            <ReferenceLine
              y={0}
              stroke="#7890a8"
              strokeDasharray="4 4"
              label={{
                value: '기준선',
                fill: '#9fb1c4',
                position: 'insideTopRight',
                fontSize: 11,
              }}
            />
            <Tooltip
              formatter={(value: number) => `${value.toFixed(2)}%`}
              labelFormatter={formatTooltipLabel}
              contentStyle={{
                background: '#071018',
                borderColor: '#223548',
                borderRadius: 8,
                color: '#dbe7f3',
              }}
              labelStyle={{ color: '#dbe7f3' }}
              itemStyle={{ color: '#dbe7f3' }}
            />
            <Area
              type="monotone"
              dataKey="drawdown"
              stroke="#ef4444"
              fill="url(#drawdownGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState />
      )}
    </section>
  );
});

export default DrawdownChart;

function EmptyState() {
  return (
    <div className="performance-chart-empty">
      데이터가 없습니다.
    </div>
  );
}
